import React, { memo, useState, useCallback, useEffect } from 'react';
import { useDispatch } from 'react-redux';

import { EditInternalAtmosphericLogModal } from 'Components/RocketDry';
import {
  numberInputRegex,
  checkValidRelativeHumidity,
  checkValidTemperature,
  checkValidDewPoint,
  checkValidGPP,
  calculateDewPoint,
  calculateGPP,
} from 'Containers/RocketDry/helpers';

import { parseNumber } from 'Utils/numbers';
import { parseISODate } from 'Utils/helpers';
import { useRocketDryFunctions } from 'Context/RocketDry';
import { updateRoomAtmosphericLog } from 'Containers/RocketDry/actions';

function EditInternalAtmosphericLogModalContainer() {
  const dispatch = useDispatch();

  const {
    isEditInternalAtmosphericLogModalOpen,
    setIsEditInternalAtmosphericLogModalOpen,
    setSelectedAtmosphericLog,
    selectedAtmosphericLogGroup,
    setInternalAtmosphericLogUpdated,
  }: any = useRocketDryFunctions();

  const [inletRelativeHumidity, setInletRelativeHumidity] = useState('');
  const [inletTemperature, setInletTemperature] = useState('');
  const [inletDewPoint, setInletDewPoint] = useState('');
  const [inletGPP, setInletGPP] = useState('');

  const [outletRelativeHumidity, setOutletRelativeHumidity] = useState('');
  const [outletTemperature, setOutletTemperature] = useState('');
  const [outletDewPoint, setOutletDewPoint] = useState('');
  const [outletGPP, setOutletGPP] = useState('');

  const [recordTime, setRecordTime] = useState(undefined);

  const [formErrors, setFormErrors] = useState({});

  useEffect(() => {
    if (selectedAtmosphericLogGroup) {
      const { inlet, outlet } = selectedAtmosphericLogGroup;
      if (!inlet.emptyEntry) {
        setInletRelativeHumidity(inlet.relativeHumidity ?? '');
        setInletTemperature(inlet.temperature ?? '');
        setInletDewPoint(inlet.dewPoint ?? '');
        setInletGPP(inlet.gpp ?? '');
      }
      if (outlet) {
        setOutletRelativeHumidity(outlet.relativeHumidity ?? '');
        setOutletTemperature(outlet.temperature ?? '');
        setOutletDewPoint(outlet.dewPoint ?? '');
        setOutletGPP(outlet.gpp ?? '');
      }

      const formattedRecordTime = inlet?.createdAt ? parseISODate(inlet.createdAt) : undefined;
      setRecordTime(formattedRecordTime);
    }
  }, [selectedAtmosphericLogGroup]);

  const onChangeInletRelativeHumidity = useCallback(
    ({ target: { value } }) => {
      const filtered = value.replace(numberInputRegex, '');
      setInletRelativeHumidity(filtered);

      // auto-calculate dew point and GPP
      if (filtered && inletTemperature) {
        const dewPoint = calculateDewPoint(filtered, parseNumber(inletTemperature));
        setInletDewPoint(dewPoint.toFixed(2));
        const gpp = calculateGPP(dewPoint);
        setInletGPP(gpp.toString());
      }
    },
    [inletTemperature]
  );

  const onChangeInletTemperature = useCallback(
    ({ target: { value } }) => {
      const filtered = value.replace(numberInputRegex, '');
      setInletTemperature(filtered);

      if (filtered && inletRelativeHumidity) {
        const dewPoint = calculateDewPoint(parseNumber(inletRelativeHumidity), filtered);
        setInletDewPoint(dewPoint.toFixed(2));
        const gpp = calculateGPP(dewPoint);
        setInletGPP(gpp.toString());
      }
    },
    [inletRelativeHumidity]
  );

  const onChangeInletDewPoint = useCallback(({ target: { value } }) => {
    const filtered = value.replace(numberInputRegex, '');
    setInletDewPoint(filtered);

    // auto-calculate GPP
    if (filtered) {
      const gpp = calculateGPP(filtered);
      setInletGPP(gpp.toString());
    }
  }, []);

  const onChangeInletGPP = useCallback(({ target: { value } }) => {
    const filtered = value.replace(numberInputRegex, '');
    setInletGPP(filtered);
  }, []);

  const onChangeOutletRelativeHumidity = useCallback(
    ({ target: { value } }) => {
      const filtered = value.replace(numberInputRegex, '');
      setOutletRelativeHumidity(filtered);

      if (filtered && outletTemperature) {
        const dewPoint = calculateDewPoint(filtered, parseNumber(outletTemperature));
        setOutletDewPoint(dewPoint.toFixed(2));
        const gpp = calculateGPP(dewPoint);
        setOutletGPP(gpp.toString());
      }
    },
    [outletTemperature]
  );

  const onChangeOutletTemperature = useCallback(
    ({ target: { value } }) => {
      const filtered = value.replace(numberInputRegex, '');
      setOutletTemperature(filtered);

      if (filtered && outletRelativeHumidity) {
        const dewPoint = calculateDewPoint(parseNumber(outletRelativeHumidity), filtered);
        setOutletDewPoint(dewPoint.toFixed(2));
        const gpp = calculateGPP(dewPoint);
        setOutletGPP(gpp.toString());
      }
    },
    [outletRelativeHumidity]
  );

  const onChangeOutletDewPoint = useCallback(({ target: { value } }) => {
    const filtered = value.replace(numberInputRegex, '');
    setOutletDewPoint(filtered);

    if (filtered) {
      const gpp = calculateGPP(filtered);
      setOutletGPP(gpp.toString());
    }
  }, []);

  const onChangeOutletGPP = useCallback(({ target: { value } }) => {
    const filtered = value.replace(numberInputRegex, '');
    setOutletGPP(filtered);
  }, []);

  const onCloseModal = useCallback(() => {
    setIsEditInternalAtmosphericLogModalOpen(false);
    setSelectedAtmosphericLog({});
    setFormErrors({});
  }, []);

  const onSaveInternalButtonClick = useCallback(async () => {
    // Similar process to adding logs,  we need to validate the values beforehand.
    const canCreate =
      inletRelativeHumidity ||
      inletTemperature ||
      inletDewPoint ||
      inletGPP ||
      outletRelativeHumidity ||
      outletTemperature ||
      outletDewPoint ||
      outletGPP;
    if (canCreate) {
      const errors: any = {};
      if (inletRelativeHumidity && !checkValidRelativeHumidity(parseFloat(inletRelativeHumidity))) {
        errors.inletRelativeHumidity = 'Relative humidity should be between 0 and 100.';
      }
      if (inletTemperature && !checkValidTemperature(parseFloat(inletTemperature))) {
        errors.inletTemperature = 'Temperature should be between -999 and 999.';
      }
      if (inletDewPoint && !checkValidDewPoint(parseFloat(inletDewPoint))) {
        errors.inletDewPoint = 'Dew point should be between -999 and 999.';
      }
      if (inletGPP && !checkValidGPP(parseFloat(inletGPP))) {
        errors.inletGPP = 'GPP should be between 0 and 999.';
      }

      if (outletRelativeHumidity && !checkValidRelativeHumidity(parseFloat(outletRelativeHumidity))) {
        errors.outletRelativeHumidity = 'Relative humidity should be between 0 and 100.';
      }
      if (outletTemperature && !checkValidTemperature(parseFloat(outletTemperature))) {
        errors.outletTemperature = 'Temperature should be between -999 and 999.';
      }
      if (outletDewPoint && !checkValidDewPoint(parseFloat(outletDewPoint))) {
        errors.outletDewPoint = 'Dew point should be between -999 and 999.';
      }
      if (outletGPP && !checkValidGPP(parseFloat(outletGPP))) {
        errors.outletGPP = 'GPP should be between 0 and 999.';
      }
      setFormErrors(errors);

      if (Object.keys(errors).length === 0) {
        const createdAt = new Date(recordTime).toISOString();
        await dispatch(
          updateRoomAtmosphericLog(
            selectedAtmosphericLogGroup.inlet.id,
            inletRelativeHumidity,
            inletTemperature,
            inletDewPoint,
            inletGPP,
            createdAt
          )
        );

        if (selectedAtmosphericLogGroup.outlet) {
          await dispatch(
            // no need to update createdAt this time because backend automatically does that when updating inlet
            updateRoomAtmosphericLog(
              selectedAtmosphericLogGroup.outlet.id,
              outletRelativeHumidity,
              outletTemperature,
              outletDewPoint,
              outletGPP
            )
          );
        }

        setInternalAtmosphericLogUpdated(true);
        onCloseModal();
      }
    }
  }, [
    selectedAtmosphericLogGroup,
    inletRelativeHumidity,
    inletTemperature,
    inletDewPoint,
    inletGPP,
    outletRelativeHumidity,
    outletTemperature,
    outletDewPoint,
    outletGPP,
    recordTime,
  ]);

  return (
    <EditInternalAtmosphericLogModal
      isOpen={isEditInternalAtmosphericLogModalOpen}
      closeModal={onCloseModal}
      inletRelativeHumidity={inletRelativeHumidity}
      inletTemperature={inletTemperature}
      inletDewPoint={inletDewPoint}
      inletGPP={inletGPP}
      outletRelativeHumidity={outletRelativeHumidity}
      outletTemperature={outletTemperature}
      outletDewPoint={outletDewPoint}
      outletGPP={outletGPP}
      recordTime={recordTime}
      formErrors={formErrors}
      onSaveInternalButtonClick={onSaveInternalButtonClick}
      onChangeInletRelativeHumidity={onChangeInletRelativeHumidity}
      onChangeInletTemperature={onChangeInletTemperature}
      onChangeInletDewPoint={onChangeInletDewPoint}
      onChangeInletGPP={onChangeInletGPP}
      onChangeOutletRelativeHumidity={onChangeOutletRelativeHumidity}
      onChangeOutletTemperature={onChangeOutletTemperature}
      onChangeOutletDewPoint={onChangeOutletDewPoint}
      onChangeOutletGPP={onChangeOutletGPP}
      onChangeRecordTime={setRecordTime}
    />
  );
}

const EditInternalAtmosphericLogModalMemo = memo(EditInternalAtmosphericLogModalContainer);

export { EditInternalAtmosphericLogModalMemo as EditInternalAtmosphericLogModal };
